/**
 * Seeded weekly targets for the Targets board (Prompt F, Stage 3).
 *
 * ASSUMPTION: these are demo placeholders. The client sets the real per-studio,
 * per-artist numbers - the ops team builds this by hand today ("manually putting
 * in per studio, per artist the amount of bookings and then the dollar"). What
 * matters here is the board maths: red/green vs target, the elapsed-days pace
 * tally, and the average-job-value column that catches the $20k-shortfall
 * pattern (session count on target while dollars quietly fall short).
 *
 * The target week runs Thursday -> Tuesday (6 days), reviewed on Wednesday.
 */
import { REVIEWS_WEEKLY_TARGET } from '@/data/reviewsFixture';

export interface ArtistWeeklyTarget {
  sessionsPerWeek: number;
  dollarsPerWeek: number;
  /** Deposit-backed sessions target (Zack's DEPOSITS number, counted as sessions). */
  depositSessionsPerWeek: number;
}

/**
 * Seed rebuild Commit C: scaled to the SMALL seed's actual in-window completed
 * volume so the Targets board reads a real red/green SPREAD instead of all-red
 * wallpaper. Over the 6-day target week the demo network completes only ~1
 * session per artist, so a 12/$6,000 target buried every studio in red. At
 * ~1 session / ~$900 per artist the healthy stores (Coburg, most flagships)
 * meet pace while the troubled store (Frankston) clearly misses. The threshold
 * IS the health signal - these stay demo placeholders the client re-tunes to
 * their real per-studio, per-artist numbers.
 */
export const STUDIO_DEFAULT_TARGET: ArtistWeeklyTarget = {
  sessionsPerWeek: 1,
  dollarsPerWeek: 900,
  depositSessionsPerWeek: 1,
};

/**
 * Per-artist overrides; the Coburg hero cast (the golden-day payout showcase
 * artists, seed rebuild Commit B) carry higher star-artist targets than the
 * studio default, so their rows read as the busy top-billers they are while the
 * studio total still lands comfortably green on the small seed.
 */
export const ARTIST_TARGET_OVERRIDES: Record<string, ArtistWeeklyTarget> = {
  'coburg-jude': { sessionsPerWeek: 2, dollarsPerWeek: 1500, depositSessionsPerWeek: 2 },
  'coburg-theo': { sessionsPerWeek: 2, dollarsPerWeek: 1500, depositSessionsPerWeek: 2 },
  'coburg-kai': { sessionsPerWeek: 2, dollarsPerWeek: 1500, depositSessionsPerWeek: 2 },
  'coburg-lena': { sessionsPerWeek: 2, dollarsPerWeek: 1500, depositSessionsPerWeek: 2 },
};

export function weeklyTargetFor(artistId: string): ArtistWeeklyTarget {
  return ARTIST_TARGET_OVERRIDES[artistId] ?? STUDIO_DEFAULT_TARGET;
}

/**
 * Editable reporting-targets config (Prompt G follow-up). Seeds the brain-store
 * slice the Settings Targets tab writes to; the reporting selectors read the live
 * slice, not these constants, so client edits flow straight through. These stay as
 * the seed defaults (and the loadFixtures/reset fallback).
 */
export interface ReportingTargetsConfig {
  studioDefault: ArtistWeeklyTarget;
  artistOverrides: Record<string, ArtistWeeklyTarget>;
  /** Per-studio weekly reviews target; falls back to `reviewsDefault`. */
  reviewsByStudio: Record<string, number>;
  reviewsDefault: number;
}

export const DEFAULT_REPORTING_TARGETS: ReportingTargetsConfig = {
  studioDefault: STUDIO_DEFAULT_TARGET,
  artistOverrides: ARTIST_TARGET_OVERRIDES,
  reviewsByStudio: {},
  reviewsDefault: REVIEWS_WEEKLY_TARGET,
};

/**
 * Lead-threshold bands (Prompt G, Stage 5, item 17). Higher is better - how many
 * new clients an artist booked this week. ASSUMPTION: 0-2 = meeting, 3-5 = good,
 * 6+ = very good; the client sets the real edges.
 */
export type LeadThresholdBand = 'meet' | 'acceptable' | 'very-good';

export const LEAD_BAND_EDGES = { acceptableFrom: 3, veryGoodFrom: 6 } as const;

export function leadThresholdBand(count: number): LeadThresholdBand {
  if (count >= LEAD_BAND_EDGES.veryGoodFrom) return 'very-good';
  if (count >= LEAD_BAND_EDGES.acceptableFrom) return 'acceptable';
  return 'meet';
}

export const LEAD_BAND_LABEL: Record<LeadThresholdBand, string> = {
  meet: 'Meeting',
  acceptable: 'Good',
  'very-good': 'Very good',
};

/**
 * Alert bands (Prompt G, Stage 5, item 19). ASSUMPTION: placeholder thresholds
 * the client sets. A breach fires a single channel ping per breach per day.
 */
export const ALERT_BANDS = {
  /** Reschedules in the window above this count is a leak-rate breach. */
  reschedulesAbove: 5,
  /** Dateless (paid, no-date) deposits above this count is a money-on-file breach. */
  datelessAbove: 8,
  /**
   * An unpaid artist envelope this many days old escalates: red on the payout card
   * and one channel ping. Two days of grace absorbs a normal quiet patch (client,
   * 30 Jul); the third day is a pattern, not a blip.
   */
  payoutArrearsDays: 3,
} as const;
